import React, {useEffect, useState} from 'react';
import { ResultMetric } from '../../../types/cards';
import { XAxis, YAxis, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from 'recharts';
import './ResultContent.css';
import {useTranslation} from "react-i18next";
import {useUserChoicesStore} from "../../../store/userChoicesStore";

interface ResultContentProps {
    title?: string;
    description?: string;
    metrics?: ResultMetric[];
    resultLevel?: string;
    resultScore?: number;
    step: any;
    resultType?: 'PROFILE_ASSESSMENT' | 'DEFAULT' | 'GROWTH_PROJECTION' | 'GROWTH_CHART';
    goal?: {
        emoji: string;
        text: string;
    };
    targetDate?: string;
    progressPoints?: Array<{
        month: string;
        level: string;
        isHighlighted?: boolean;
    }>;
    buttonText?: string;
    weeks?: Array<{
        label: string;
        isNow?: boolean;
        isAfter?: boolean;
    }>;
    onContinue: () => void;
    scoreLevel?: number | string;
}

const ResultContent: React.FC<ResultContentProps> = ({
    title,
    description,
    metrics,
    resultLevel = 'INTERMEDIATE', // Default value for the demo
    resultScore,
    resultType = 'DEFAULT',
    goal,
    targetDate,
    progressPoints,
    buttonText = 'CONTINUE',
    weeks,
    onContinue,
    scoreLevel
}) => {
    const { t} = useTranslation()
    const [goalLocal, setGoalLocal] = useState<any>();
    const { choices } = useUserChoicesStore();

    // Generate dynamic months starting from current month
    const getDynamicMonths = () => {
        const now = new Date();
        const monthNames = [
            t('months.jan'), t('months.feb'), t('months.mar'), t('months.apr'),
            t('months.may'), t('months.jun'), t('months.jul'), t('months.aug'),
            t('months.sep'), t('months.oct'), t('months.nov'), t('months.dec')
        ];

        return [
            { name: monthNames[now.getMonth()], value: 25, label: t('skills.beginner') },
            { name: monthNames[(now.getMonth() + 1) % 12], value: 45 },
            { name: monthNames[(now.getMonth() + 2) % 12], value: 70, label: t('skills.aiMaster') },
            { name: monthNames[(now.getMonth() + 3) % 12], value: 95 }
        ];
    };

    useEffect(()=> {
        // Search for goal in choices array
        const goalChoice = choices.find(choice => choice?.goal === true);
        if (goalChoice?.optionValue) {
            setGoalLocal(goalChoice);
        }
    }, [choices])

    // Helper to map scoreLevel to left percent
    const getScoreLevelPosition = (level?: number | string) => {
        if (typeof level === 'number' && !isNaN(level)) {
            // Clamp between 0 and 100
            const percent = Math.max(0, Math.min(100, level));
            return `${percent}%`;
        }
        switch ((level || '').toString().toUpperCase()) {
            case 'LOW':
                return '15%';
            case 'INTERMEDIATE':
                return '50%';
            case 'HIGH':
                return '85%';
            default:
                return '50%';
        }
    };

    const renderDefaultResult = () => (
        <div className="result-content">
            <div className="result-content__header">
                <h2 className="result-content__title">{title}</h2>
                {description && <p className="result-content__description">{description}</p>}
            </div>

            {metrics && metrics.length > 0 && (
                <div className="result-content__metrics">
                    {metrics.map((metric, index) => (
                        <div key={index} className="result-content__metric">
                            <span className="result-content__metric-icon">{metric.icon}</span>
                            <div className="result-content__metric-details">
                                <span className="result-content__metric-label">{metric.label}</span>
                                <span className="result-content__metric-value">{metric.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <button
                className="result-content__continue w-full h-14 md:h-12 px-6 md:px-4 text-base md:text-sm font-bold uppercase bg-blue-600 text-white shadow-md hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={onContinue}
                tabIndex={0}
                aria-label="Продовжити"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onContinue(); }}
            >
                {buttonText}
            </button>
        </div>
    );

    const renderProfileAssessment = () => (
        <div className="result-content result-content--profile">
            <div className="result-content__header">
                <h2 className="result-content__title">
                    {title}
                </h2>
                {description && (
                    <p className="result-content__description">
                        {description}
                    </p>
                )}
            </div>

            <div className="result-content__goal-box">
                <div className="result-content__goal">
                    <span className="result-content__goal-label">{t('yourGoal')}:</span>{' '}
                    <span className="result-content__goal-emoji">{(goalLocal && goalLocal?.emoji) || '❓'}</span>
                    <span className="result-content__goal-text">{goal?.text || goalLocal?.optionValue || 'Buy a car'}</span>
                </div>
            </div>

            <div className="result-content__progress-chart">
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                        data={getDynamicMonths()}
                        margin={{ top: 50, right: 30, left: 30, bottom: 30 }}
                        barSize={32}
                    >
                        <CartesianGrid stroke="#D3D7DF" strokeWidth={2} vertical={false} />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#A3ACB9', fontSize: 14, fontWeight: 400 }}
                            dy={10}
                        />
                        <YAxis hide domain={[0, 100]} />
                        <Bar
                            dataKey="value"
                            radius={[8, 8, 8, 8]}
                            background={{ fill: 'transparent' }}
                        >
                            {getDynamicMonths().map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        index === 0 ? '#FF4B4B' :
                                        index === 1 ? '#FFAA46' :
                                        index === 2 ? '#A3C84A' :
                                        '#46D16A'
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {/* Overlay custom labels above bars */}
            <div style={{ position: 'relative', width: '100%', height: 0, top: '-270px', pointerEvents: 'none' }}>
                {getDynamicMonths().map((entry, index) =>
                    entry.label ? (
                        <div
                            key={index}
                            className="result-content__bar-label"
                            style={{
                                position: 'absolute',
                                left: index === 0 ?  `calc(${(index + 0.5) * 25}% - 27px)`: `calc(${(index + 0.5) * 26.5}%)`,
                                top: index === 0 ? 116: 20,
                                zIndex: 10
                            }}
                        >
                            {entry.label}
                            <span className="result-content__bar-label-arrow"></span>
                        </div>
                    ) : null
                )}
            </div>
            {buttonText && (
                <div className="inform-selector__button-fixed">
            <button
                className="result-content__continue w-full rounded-[12px] md:rounded-[10px] h-14 md:h-12 px-6 md:px-4 text-base md:text-sm font-bold uppercase bg-blue-600 text-white shadow-md hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={onContinue}
                tabIndex={0}
                aria-label="Продовжити"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onContinue(); }}
            >
                {buttonText}
            </button>
                </div>)
            }
        </div>
    );

    const renderGrowthProjection = () => (
        <div className="result-content result-content--projection">
            <div className="result-content__header">
                <h2 className="result-content__title">
                    {title}
                </h2>
            </div>

            <div className="result-content__box">
                <div className="result-content__readiness">
                    <h3 className="result-content__readiness-title">{t('progress.title')}</h3>

                    <div className="result-content__readiness-bar-container">
                        <div className="result-content__readiness-bar">
                            <div className="result-content__readiness-gradient"></div>
                            <div className="result-content__readiness-indicator" style={{ left: getScoreLevelPosition(scoreLevel) }}>
                                <div className="result-content__readiness-label">{t('progress.label')}</div>
                                <div className="result-content__readiness-indicator-point"></div>
                            </div>
                        </div>
                        <div className="result-content__readiness-levels">
                            <span>{t('progress.low')}</span>
                            <span>{t('progress.intermediate')}</span>
                            <span>{t('progress.high')}</span>
                        </div>
                    </div>
                </div>

                <div className="result-content__metrics">
                    {(metrics || []).map((metric, index) => (
                        <div key={index} className="result-content__metric">
                            <div className="result-content__metric-icon-wrapper">
                                {metric.icon && typeof metric.icon === 'string' && metric.icon.startsWith('http') ? (
                                    <img src={metric.icon} alt={metric.label} className="result-content__metric-icon" />
                                ) : (
                                    <span className="result-content__metric-icon">{metric.icon}</span>
                                )}
                            </div>
                            <div className="result-content__metric-details">
                                <span className="result-content__metric-label">{metric.label}</span>
                                <span className="result-content__metric-value">{metric.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {buttonText && (
                <div className="inform-selector__button-fixed">
            <button
                className="result-content__continue w-full rounded-[12px] md:rounded-[10px] h-14 md:h-12 px-6 md:px-4 text-base md:text-sm font-bold uppercase bg-blue-600 text-white shadow-md hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={onContinue}
                tabIndex={0}
                aria-label="Continue"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onContinue(); }}
            >
                {t('continueButtonText')}
            </button>
                </div>)}
        </div>
    );

    const renderGrowthChart = () => (
        <div className="result-content result-content--chart">
            <div className="result-content__header">
                <h2 className="result-content__title">
                    {title}
                </h2>
                <p className="result-content--chart__description">
                        {description}
                    </p>
            </div>
            <div className="result-content__chart">
                <h3 className="result-content__chart-title">{t('growthChart.readinessLevel')}</h3>
                <div className="result-content__chart-container" style={{ position: 'relative', height: 220 }}>
                    <svg width="100%" height="160" viewBox="0 0 290 160" style={{ overflow: 'visible' }}>
                        <defs>
                            <linearGradient id="curveGradient" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#FF4B4B" />
                                <stop offset="50%" stopColor="#FFAA46" />
                                <stop offset="100%" stopColor="#46D16A" />
                            </linearGradient>
                        </defs>
                        {/* Horizontal grid lines */}
                        <line x1="0" y1="0" x2="290" y2="0" stroke="#DDE3F3" strokeWidth="2" />
                        <line x1="0" y1="40" x2="290" y2="40" stroke="#DDE3F3" strokeWidth="2" />
                        <line x1="0" y1="80" x2="290" y2="80" stroke="#DDE3F3" strokeWidth="2" />
                        <line x1="0" y1="120" x2="290" y2="120" stroke="#DDE3F3" strokeWidth="2" />
                        {/* S-curve path */}
                        <path
                            d="M3.03996 125C2.47534 125 2.01387 124.601 2.0003 124.098C1.98672 123.588 2.44276 123.167 3.01824 123.155C105.016 121.089 152.027 83.1075 193.502 49.5983C225.666 23.6088 253.449 1.16508 299.982 2.02386C300.557 2.03348 301.013 2.45686 301 2.96443C300.989 3.46719 300.525 3.86651 299.96 3.86651C299.952 3.86651 299.946 3.86651 299.938 3.86651C254.234 3.01735 226.741 25.2373 194.905 50.9598C174.858 67.1563 152.138 85.5154 122.151 99.6239C87.8281 115.77 48.8774 124.071 3.06982 125C3.06167 125 3.05353 125 3.04539 125H3.03996Z"
                            stroke="url(#curveGradient)"
                            strokeWidth="6"
                            fill="none"
                        />
                        {/* Now point */}
                        <circle cx="4" cy="125" r="8" fill="#fff" stroke="#E5E7EB" strokeWidth="4" />
                        {/* After 4 weeks point */}
                        <circle cx="300" cy="4" r="8" fill="#fff" stroke="#E5E7EB" strokeWidth="4" />
                    </svg>
                    {/* Now label */}
                    <div style={{ position: 'absolute', left: 30, top: 80, transform: 'translateX(-50%)', maxWidth: 120 }}>
                        <div className="result-content__chart-label result-content__chart-label--now">
                            {t('growthChart.now')}
                            <span className="result-content__bar-label-arrow" style={{ bottom: -10, top: 'auto' }}></span>
                        </div>
                    </div>
                    {/* After 4 weeks label */}
                    <div style={{ position: 'absolute', left: 'calc(100% - 30px)', top: 0, transform: 'translateX(-50%)', maxWidth: 160 }}>
                        <div className="result-content__chart-label result-content__chart-label--after">
                            {t('growthChart.afterWeeks')}
                            <span className="result-content__bar-label-arrow" style={{ bottom: -10, left: '82%', top: 'auto' }}></span>
                        </div>
                    </div>
                    {/* Week labels */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 64, display: 'flex', justifyContent: 'space-between', padding: '0 12px' }}>
                        <span style={{ color: '#5F6368', fontSize: 14 }}>{t('growthChart.week1')}</span>
                        <span style={{ color: '#5F6368', fontSize: 14 }}>{t('growthChart.week2')}</span>
                        <span style={{ color: '#5F6368', fontSize: 14 }}>{t('growthChart.week3')}</span>
                        <span style={{ color: '#5F6368', fontSize: 14 }}>{t('growthChart.week4')}</span>
                    </div>
                </div>
            </div>
            {buttonText && (
                <div className="inform-selector__button-fixed">
            <button
                className="result-content__continue w-full rounded-[12px] md:rounded-[10px] h-14 md:h-12 px-6 md:px-4 text-base md:text-sm font-bold uppercase bg-blue-600 text-white shadow-md hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={onContinue}
                tabIndex={0}
                aria-label="Продовжити"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onContinue(); }}
            >
                {t('continueButtonText')}
            </button>
                </div>)}
        </div>
    );

    switch (resultType) {
        case 'PROFILE_ASSESSMENT':
            return renderProfileAssessment();
        case 'GROWTH_PROJECTION':
            return renderGrowthProjection();
        case 'GROWTH_CHART':
            return renderGrowthChart();
        default:
            return renderDefaultResult();
    }
};

export default ResultContent;
